<template>
    <div class="m-0">
        <test-edit-settings-item
            class="mb-0 mt-0"
            :title="trans.edit_form.all_question_is_required_label"
            :is-fullscreen="isFullScreenTest"
        >
            <toggle-all-questions-is-required :disabled="!isCanEdit"/>
        </test-edit-settings-item>

        <test-edit-settings-item
            class="mb-0 mt-1"
            :title="trans.edit_form.random_order_questions_label"
            :is-fullscreen="isFullScreenTest"
        >
            <toggle-button
                v-model="testSettings.is_random_order"
                :is-disabled="isSettingsChangeInProgress || !isCanEdit"
            />
        </test-edit-settings-item>

        <test-edit-settings-item
            class="mb-0 mt-1"
            :title="trans.edit_form.random_order_answers_label"
            :is-fullscreen="isFullScreenTest"
        >
            <toggle-all-answer-random-order :disabled="!isCanEdit"/>
        </test-edit-settings-item>

        <test-edit-settings-item
            class="mb-0 mt-1"
            :title="trans.edit_form.is_has_points_label"
            :help-text="trans.edit_form.is_has_points_description"
            :is-fullscreen="isFullScreenTest"
        >
            <toggle-all-questions-is-has-points :disabled="!isCanEdit"/>
        </test-edit-settings-item>

        <test-edit-settings-item
            class="mb-0 mt-1"
            :title="trans.edit_form.can_replay_label"
            :help-text="trans.edit_form.can_replay_description"
            :is-fullscreen="isFullScreenTest"
        >
            <toggle-button
                v-model="testSettings.is_can_replay"
                :is-disabled="isSettingsChangeInProgress || !isCanEdit"
            />
        </test-edit-settings-item>

        <test-edit-settings-item
            v-if="testSettings.is_can_replay"
            class="mb-0 mt-1"
            :title="trans.edit_form.replay_period_label"
            :is-fullscreen="isFullScreenTest"
        >
            <div class="select">
                <select v-model="testSettings.replay_period_type" :disabled="isSettingsChangeInProgress || !isCanEdit" class="input-text">
                    <option v-for="type of replayPeriodTypes" :value="type.code">
                        {{ getRelayPeriodTypeTranslation(type) }}
                    </option>
                </select>
            </div>
        </test-edit-settings-item>

        <test-edit-settings-item
            v-if="!testSettings.is_can_replay || testSettings.replay_period_type !== 'all_time'"
            class="mb-0 mt-1"
            :title="trans.edit_form.not_allow_replay_users_label"
            :is-fullscreen="isFullScreenTest"
        >
            <button class="button show-not-allow-replay-users-button" @click.stop="showNotAllowReplayTestUsers">
                <span class="icon icon-show-not-allow-replay-users is-small">
                    <i class="fas fa-eye"></i>
                </span>
            </button>
        </test-edit-settings-item>

        <test-edit-settings-item
            class="mb-0 mt-1"
            :title="trans.edit_form.availability_for_unregistered_users"
            :help-text="trans.edit_form.availability_for_unregistered_users_description"
            :is-fullscreen="isFullScreenTest"
        >
            <toggle-button
                v-model="testSettings.is_public"
                :is-disabled="isSettingsChangeInProgress || !isCanEdit"
            />
        </test-edit-settings-item>

        <test-edit-settings-item
            v-if="testSettings.is_public"
            class="mb-0 mt-1"
            :title="trans.edit_form.link_to_test"
            :help-text="trans.edit_form.link_to_test_description"
            :is-fullscreen="isFullScreenTest"
        >
            <copy-input
                :copy-text="testSettings.public_link"
            />
        </test-edit-settings-item>

        <test-edit-settings-item
            v-if="testSettings.is_public"
            class="mb-0 mt-1"
            :title="trans.edit_form.can_anonymous"
            :help-text="trans.edit_form.can_anonymous_description"
            :is-fullscreen="isFullScreenTest"
        >
            <toggle-button
                v-model="testSettings.is_can_anonymous"
                :is-disabled="isSettingsChangeInProgress || !isCanEdit"
            />
        </test-edit-settings-item>

        <test-edit-settings-item
            class="mb-0 mt-1"
            :title="trans.edit_form.watch_result"
            :help-text="trans.edit_form.watch_result_description"
            :is-fullscreen="isFullScreenTest"
        >
            <toggle-button
                v-model="testSettings.is_can_see_result"
                :is-disabled="isSettingsChangeInProgress || !isCanEdit"
            />
        </test-edit-settings-item>

        <test-edit-settings-item
            class="mb-0 mt-1"
            :title="trans.edit_form.success_text_label"
            :is-fullscreen="isFullScreenTest"
        >
            <div class="control has-icons-right success-text" :class="{'is-loading' : isSuccessTextChangeInProgress}">
                    <textarea
                        class="textarea has-fixed-size input-text"
                        v-model="testSettings.success_text"
                        :disabled="!isCanEdit"
                    ></textarea>
                <span
                    v-show="isSuccessTextChanged && !isSuccessTextChangeInProgress"
                    class="icon is-small is-right has-text-success"
                    :class="{
                        'has-text-success': !settingsErrors.success_text,
                        'has-text-danger': settingsErrors.success_text,
                    }"
                >
                    <i v-if="!settingsErrors.success_text" class="fas fa-check"></i>
                    <i v-if="settingsErrors.success_text" class="fas fa-times"></i>
                </span>
            </div>
            <p
                v-for="(error, index) in settingsErrors.success_text"
                :key="index"
                class="help is-danger"
            >
                {{error}}
            </p>
        </test-edit-settings-item>
    </div>
</template>

<script>
import ToggleAllQuestionsIsHasPoints from "../questions/ToggleAllQuestionsIsHasPoints";
import ToggleAllQuestionsIsRequired from "../questions/ToggleAllQuestionsIsRequired";
import ToggleAllAnswerRandomOrder from "../questions/ToggleAllAnswerRandomOrder";
import ToggleButton from "../../../../utils/forms/ToggleButton";
import {mapActions, mapGetters} from "vuex";
import HelpTooltip from "../../../../utils/HelpTooltip";
import CopyInput from "../../../../utils/CopyInput";
import TestEditSettingsItem from "./TestEditSettingsItem";

export default {
    name: "TestEditSettingsTab",
    components: {
        TestEditSettingsItem,
        CopyInput,
        HelpTooltip,
        ToggleAllQuestionsIsHasPoints,
        ToggleAllQuestionsIsRequired,
        ToggleAllAnswerRandomOrder,
        ToggleButton,
    },
    data() {
        return {
            isSettingsChangeInProgress: false,
            isSuccessTextChangeTimeout: null,
            isSuccessTextChangeInProgress: false,
            isSuccessTextChanged: false,
            error: null,
        }
    },
    computed: {
        ...mapGetters('tests', [
            'testBeingEdited',
            'testEditor',
            'trans',
            'requestsProgress',
            'testSettings',
            'isFullScreenTest',
            'isCanEdit',
            'settingsErrors',
        ]),
        replayPeriodTypes() {
            return this.testEditor.parameters.replay_period_types ?? [];
        },
    },
    watch: {
        'testSettings.is_can_replay': function (value) {
            this.updateTestSettingsHandler({'is_can_replay': value});
        },
        'testSettings.is_random_order': function (value) {
            this.updateTestSettingsHandler({'is_random_order': value});
        },
        'testSettings.is_can_see_result': function (value) {
            this.updateTestSettingsHandler({'is_can_see_result': value});
        },
        'testSettings.is_public': function (value) {
            this.updateTestSettingsHandler({'is_public': value});
        },
        'testSettings.is_can_anonymous': function (value) {
            this.updateTestSettingsHandler({'is_can_anonymous': value});
        },
        'testSettings.replay_period_type': function (value) {
            this.updateTestSettingsHandler({'replay_period_type': value});
        },
        'testSettings.success_text': function (value) {
            this.isSuccessTextChangeInProgress = true;

            clearTimeout(this.isSuccessTextChangeTimeout);

            this.isSuccessTextChangeTimeout = setTimeout(() => {
                this.clearSettingsErrors();

                this.updateTestSettings({
                        test: this.testBeingEdited,
                        settings: {'success_text': value},
                    })
                    .catch((error) => {
                        this.error = error;
                    })
                    .finally(() => {
                        this.isSuccessTextChanged = true;
                        this.isSuccessTextChangeInProgress = false;
                    });
            }, 1000);

        },
    },
    methods: {
        ...mapActions('tests', [
            'updateTestSettings',
            'hideTestsManagementPage',
            'returnToTestListPage',
            'showNotAllowReplayTestUsers',
            'clearSettingsErrors',
        ]),
        updateTestSettingsHandler(settings) {
            this.isSettingsChangeInProgress = true;
            this.updateTestSettings({
                test: this.testBeingEdited,
                settings: settings,
            }).finally(() => {
                this.isSettingsChangeInProgress = false;
            });
        },
        getRelayPeriodTypeTranslation(type) {
            return this.trans.edit_form.replay_period_types[type.code] ?? type.code;
        },
    }
}
</script>

<style scoped lang="scss">
@import '../../../../../../sass/variables';

.bulma .test-edit-form .tabs-container .tabs-details {

    .show-not-allow-replay-users-button {
        &:hover {
            color: $tests-blue;
        }
    }

    .success-text {
        width: 100%;
    }

    .input-text {
        border: 1px solid $tests-light-grey;
        box-sizing: border-box;
        border-radius: 6px;
        box-shadow: none;
    }
}
</style>